import { debug, warn } from "./config.js";
import { runItemRender } from "./hooks.js";
import {
  interpolate,
  isEmptyValue,
  resolvePath,
  toAlt,
  toSrc,
  toText,
} from "./normalize.js";
import { isSafeUrl, sanitizeHtml } from "./sanitize.js";
import type { HookCtx, Item, ListConfig } from "./types.js";

/** `data-slater-bind-attr-aria-label="titre"` — bind any attribute to a field. */
const BIND_ATTR_PREFIX = "data-slater-bind-attr-";

/**
 * Attributes whose value is a URL. Bound from an image field they take its URL
 * (not its alt), and they are scheme-checked before being written.
 */
const URL_ATTRS = new Set([
  "href", "src", "srcset", "action", "formaction", "poster", "ping",
  "background", "cite", "data", "xlink:href",
]);

/**
 * Writes one `data-slater-bind-attr-*` binding.
 *
 * CMS content is authored by whoever can edit the collection, so an arbitrary
 * attribute sink needs the guards the fixed bindings don't:
 *   - `on*` would install an event handler written in the CMS.
 *   - `style` is a CSS injection sink.
 *   - a URL attribute could carry `javascript:` — checked with the same
 *     allowlist the rich-text sanitizer uses.
 *
 * An empty value REMOVES the attribute rather than writing `""`, matching how
 * `bind-src` drops a missing image: a blank `aria-label` is worse than none, and
 * a leftover value from the cloned template would belong to a different item.
 */
function bindAttribute(node: Element, name: string, raw: unknown): void {
  if (name.startsWith("on") || name === "style") {
    warn(
      `${BIND_ATTR_PREFIX}${name} is not allowed — event handlers and style ` +
        `cannot be driven by CMS content. Ignored.`,
    );
    return;
  }

  const isUrl = URL_ATTRS.has(name);
  const value = isUrl ? toSrc(raw) : toText(raw);

  if (!value) {
    node.removeAttribute(name);
    return;
  }
  if (isUrl && !isSafeUrl(value)) {
    warn(`${BIND_ATTR_PREFIX}${name}: unsafe URL scheme in "${value}". Ignored.`);
    node.removeAttribute(name);
    return;
  }
  node.setAttribute(name, value);
}

/**
 * Finds the node to clone per item.
 *
 * Prefers an explicit `[data-slater-template]`. Falling back to the first
 * `.w-dyn-item` is convenient but unsafe on any list using Webflow's conditional
 * visibility: a node that only exists when a field is non-empty (Orisha's
 * `.card_blog_eyebrow`) is absent from the template whenever the first item
 * happens to lack that field — so no rendered card could ever show it. Hence the
 * warning.
 */
export function findTemplate(results: Element): Element | null {
  const explicit = results.querySelector("[data-slater-template]");
  if (explicit) return explicit;

  const first = results.querySelector(".w-dyn-item");
  if (first) {
    warn(
      "no [data-slater-template] found; cloning the first .w-dyn-item. If your card " +
        "uses Webflow conditional visibility, mark the template explicitly — an " +
        "optional node missing from the first item can never render.",
    );
    return first;
  }
  return null;
}

/** Picks the template to clone for one item — search results mix collections. */
export type TemplatePicker = (item: Item) => Element;

/**
 * Collects a container's authored `[data-slater-template]` cards into a
 * per-collection picker: `data-slater-template="blog"` renders only items whose
 * `item.collection` is "blog"; the valueless template is the default (with no
 * valueless card, the first keyed one doubles as the default so a container
 * built entirely from per-collection cards still renders everything).
 *
 * Shared by global-search blocks and group lists — the two block kinds whose
 * items mix collections. `removeOriginals`: search blocks remove the authored
 * cards at collection time (design-time scaffolding in a static container whose
 * replace path only clears `[data-slater-rendered]` cards); group lists pass
 * false — the list replace path replaceChildren()s the container on first
 * success, and until then the authored cards are the page's fallback, exactly
 * like a single template card in a CMS list.
 */
export function buildTemplatePicker(
  results: Element,
  removeOriginals: boolean,
): TemplatePicker | null {
  const nodes = Array.from(results.querySelectorAll("[data-slater-template]"));
  let fallback: Element | null = null;
  const byCollection = new Map<string, Element>();

  for (const node of nodes) {
    const collection = node.getAttribute("data-slater-template") ?? "";
    const clone = node.cloneNode(true) as Element;
    if (!collection) {
      fallback ??= clone;
    } else if (!byCollection.has(collection)) {
      byCollection.set(collection, clone);
    }
    if (removeOriginals) node.remove();
  }

  const defaultTemplate = fallback ?? byCollection.values().next().value ?? null;
  if (!defaultTemplate) return null;

  return (item) => {
    const key = typeof item.collection === "string" ? item.collection : "";
    return byCollection.get(key) ?? defaultTemplate;
  };
}

/**
 * Points an element at a URL.
 *
 * Anchors navigate natively, so we only set `href`. A non-anchor — a Webflow
 * "clickable" overlay built from a `<button>` or `<div>` — ignores `href`, so it
 * also gets a click handler that navigates. Honours `target="_blank"` and lets
 * modifier/middle clicks through.
 */
function setLink(node: Element, href: string): void {
  if (!href) return;
  node.setAttribute("href", href);
  if (node.tagName === "A") return;

  const el = node as HTMLElement;
  if (el.dataset.slaterLinked === "true") return;
  el.dataset.slaterLinked = "true";
  if (!el.style.cursor) el.style.cursor = "pointer";
  el.addEventListener("click", (event) => {
    const mouse = event as MouseEvent;
    if (event.defaultPrevented || mouse.button > 1 || mouse.metaKey || mouse.ctrlKey) return;
    if (node.getAttribute("target") === "_blank") window.open(href, "_blank", "noopener");
    else window.location.assign(href);
  });
}

function applyBinding(node: Element, item: Item, config: ListConfig): void {
  const textPath = node.getAttribute("data-slater-bind-text");
  if (textPath !== null) node.textContent = toText(resolvePath(item, textPath));

  const htmlPath = node.getAttribute("data-slater-bind-html");
  if (htmlPath !== null) {
    const raw = resolvePath(item, htmlPath);
    node.innerHTML = typeof raw === "string" ? sanitizeHtml(raw) : toText(raw);
  }

  const srcPath = node.getAttribute("data-slater-bind-src");
  if (srcPath !== null) {
    const url = toSrc(resolvePath(item, srcPath));
    // Always drop the template's srcset — a stale one would override our src, or
    // (when this item has no image) keep showing the placeholder photo.
    node.removeAttribute("srcset");
    if (url) {
      node.setAttribute("src", url);
    } else {
      // No image on this item. Remove src entirely rather than leaving the
      // cloned template's photo (which belongs to a different item) — that's the
      // "wrong-coloured car" illusion. Removing (not blanking) avoids a broken
      // icon / page re-request. Add data-slater-hide-empty to collapse the node.
      node.removeAttribute("src");
    }
  }

  const altPath = node.getAttribute("data-slater-bind-alt");
  if (altPath !== null) node.setAttribute("alt", toAlt(resolvePath(item, altPath)));

  // Any other attribute: data-slater-bind-attr-aria-label="titre". Iterate a
  // SNAPSHOT — bindAttribute writes attributes, which mutates the live map.
  for (const attr of Array.from(node.attributes)) {
    if (!attr.name.startsWith(BIND_ATTR_PREFIX)) continue;
    const target = attr.name.slice(BIND_ATTR_PREFIX.length);
    if (target) bindAttribute(node, target, resolvePath(item, attr.value));
  }

  const hrefPath = node.getAttribute("data-slater-bind-href");
  if (hrefPath !== null) {
    const value = resolvePath(item, hrefPath);
    setLink(node, toText(value));
  }

  // List items carry no `url` field — only a slug — so a card link is composed
  // from a pattern such as `/{locale}/blog/{slug}`. Search results AND group
  // items DO carry a server-computed `url` (a page's published path, or the
  // collection's detail directory + slug), which is what a bare
  // data-slater-item-href points at there — an authored pattern still wins via
  // the first branch, and a null url means "no link", never a guessed 404.
  // Never pass the url through a `{url}` pattern — interpolate percent-encodes
  // its slashes.
  const pattern = node.getAttribute("data-slater-href") ?? (node.hasAttribute("data-slater-item-href") ? config.detailPath : null);
  if (pattern) setLink(node, interpolate(pattern, item, config.locale));
  else if (
    node.hasAttribute("data-slater-item-href") &&
    (config.mode === "search" || config.group)
  ) {
    setLink(node, toText(item.url));
  }

  if (node.hasAttribute("data-slater-hide-empty")) {
    const path =
      node.getAttribute("data-slater-hide-empty") ||
      textPath ||
      htmlPath ||
      srcPath ||
      "";
    if (path && isEmptyValue(resolvePath(item, path))) {
      (node as HTMLElement).style.display = "none";
    }
  }

  const showIf = node.getAttribute("data-slater-show-if");
  if (showIf !== null && isEmptyValue(resolvePath(item, showIf))) {
    (node as HTMLElement).style.display = "none";
  }
}

/** Clones the template and binds one item into it. */
export function renderItem(
  template: Element,
  item: Item,
  config: ListConfig,
  ctx?: HookCtx,
): Element {
  const clone = template.cloneNode(true) as Element;
  clone.removeAttribute("data-slater-template");
  // Marks embed-rendered cards, so search mode can clear exactly these and
  // nothing else from a container that also holds controls. Style hook, too.
  clone.setAttribute("data-slater-rendered", "");

  // Webflow's IX2 keys interactions off `data-w-id`. Cloning it would make one id
  // address many nodes; dropping it means the clone simply has no interaction,
  // which is far better than a half-bound one.
  clone.removeAttribute("data-w-id");
  for (const el of Array.from(clone.querySelectorAll("[data-w-id]"))) {
    el.removeAttribute("data-w-id");
  }

  const targets: Element[] = [clone, ...Array.from(clone.querySelectorAll("*"))];
  for (const node of targets) applyBinding(node, item, config);
  // The card is not in the document yet — hook mutations batch into the
  // fragment insert.
  if (ctx) runItemRender(clone, item, ctx);
  return clone;
}

/** Replaces (or appends to) the results container in one reflow. */
export function renderItems(
  results: Element,
  template: Element | TemplatePicker,
  items: Item[],
  config: ListConfig,
  mode: "replace" | "append",
  ctx?: HookCtx,
): void {
  const fragment = document.createDocumentFragment();
  for (const item of items) {
    const tpl = typeof template === "function" ? template(item) : template;
    fragment.appendChild(renderItem(tpl, item, config, ctx));
  }

  if (mode === "replace") {
    if (config.mode === "search") {
      // A search container may be the wrapper itself, with the input and the
      // idle/empty elements as siblings of the cards — replaceChildren would
      // wipe them, so only previously rendered cards are cleared.
      removeRenderedCards(results);
      results.appendChild(fragment);
    } else {
      results.replaceChildren(fragment);
    }
  } else {
    results.appendChild(fragment);
  }

  debug(`rendered ${items.length} item(s) (${mode})`);
  reinitInteractions();
}

/** Removes the embed-rendered cards from a container, leaving everything else. */
export function removeRenderedCards(results: Element): void {
  for (const el of Array.from(results.querySelectorAll(":scope > [data-slater-rendered]"))) {
    el.remove();
  }
}

/**
 * Re-runs the page's animation frameworks over the nodes we just created.
 *
 * Webflow binds IX2 at DOMContentLoaded and GSAP's ScrollTrigger caches element
 * positions; neither knows about DOM we injected afterwards.
 */
export function reinitInteractions(): void {
  try {
    window.Webflow?.require?.("ix2")?.init?.();
  } catch {
    /* Webflow absent (e.g. a test page) — nothing to re-init. */
  }
  try {
    window.ScrollTrigger?.refresh?.();
  } catch {
    /* GSAP absent. */
  }
}
